import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';

/**
 * Formik Demo Component
 * Showcases various Formik features including:
 * - Field validation with Yup
 * - Different input types
 * - Dynamic field arrays
 * - Conditional fields
 * - Custom error messages
 * - Form submission handling
 */
const FormikDemo = () => {
  const [submittedData, setSubmittedData] = useState(null);

  // Validation schema using Yup
  const validationSchema = Yup.object().shape({
    // Text inputs
    firstName: Yup.string()
      .min(2, 'Too Short!')
      .max(50, 'Too Long!')
      .required('First name is required'),
    lastName: Yup.string()
      .min(2, 'Too Short!')
      .max(50, 'Too Long!')
      .required('Last name is required'),
    
    // Email validation
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    
    // Phone validation
    phone: Yup.string()
      .matches(/^[0-9]{10}$/, 'Phone must be exactly 10 digits')
      .required('Phone is required'),
    
    // Number validation
    age: Yup.number()
      .min(18, 'Must be at least 18 years old')
      .max(100, 'Must be less than 100 years old')
      .required('Age is required'),
    
    // Select dropdown
    country: Yup.string()
      .required('Please select a country'),
    
    // Radio buttons
    gender: Yup.string()
      .required('Please select gender'),
    
    // Checkbox
    termsAccepted: Yup.boolean()
      .oneOf([true], 'You must accept the terms and conditions'),
    
    // Textarea
    bio: Yup.string()
      .min(10, 'Bio must be at least 10 characters')
      .max(200, 'Bio must be less than 200 characters'),
    
    // Date
    dateOfBirth: Yup.date()
      .max(new Date(), 'Date of birth cannot be in the future')
      .required('Date of birth is required'),
    
    // Field Array
    skills: Yup.array()
      .of(
        Yup.object().shape({
          name: Yup.string().required('Skill name is required'),
          level: Yup.string().required('Skill level is required'),
        })
      )
      .min(1, 'At least one skill is required'),
    
    // Conditional field
    hasExperience: Yup.boolean(),
    yearsOfExperience: Yup.number().when('hasExperience', {
      is: true,
      then: (schema) => schema.min(0).required('Years of experience is required'),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  // Initial form values
  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    age: '',
    country: '',
    gender: '',
    termsAccepted: false,
    bio: '',
    dateOfBirth: '',
    skills: [{ name: '', level: '' }],
    hasExperience: false,
    yearsOfExperience: '',
  };

  // Handle form submission
  const handleSubmit = (values, { setSubmitting, resetForm }) => {
    setTimeout(() => {
      console.log('Form submitted:', values);
      setSubmittedData(values);
      setSubmitting(false);
      // Optionally reset the form
      // resetForm();
    }, 1000);
  };

  return (
    <div className="formik-demo">
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, isSubmitting, setFieldValue }) => (
          <Form>
            <div className="row">
              {/* Text Inputs */}
              <div className="col-md-6 mb-3">
                <label htmlFor="firstName" className="form-label">
                  First Name *
                </label>
                <Field
                  type="text"
                  name="firstName"
                  className={`form-control ${
                    errors.firstName && touched.firstName ? 'is-invalid' : ''
                  }`}
                  placeholder="Enter first name"
                />
                <ErrorMessage
                  name="firstName"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              <div className="col-md-6 mb-3">
                <label htmlFor="lastName" className="form-label">
                  Last Name *
                </label>
                <Field
                  type="text"
                  name="lastName"
                  className={`form-control ${
                    errors.lastName && touched.lastName ? 'is-invalid' : ''
                  }`}
                  placeholder="Enter last name"
                />
                <ErrorMessage
                  name="lastName"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              {/* Email */}
              <div className="col-md-6 mb-3">
                <label htmlFor="email" className="form-label">
                  Email Address *
                </label>
                <Field
                  type="email"
                  name="email"
                  className={`form-control ${
                    errors.email && touched.email ? 'is-invalid' : ''
                  }`}
                  placeholder="example@email.com"
                />
                <ErrorMessage
                  name="email"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              {/* Phone */}
              <div className="col-md-6 mb-3">
                <label htmlFor="phone" className="form-label">
                  Phone Number *
                </label>
                <Field
                  type="text"
                  name="phone"
                  className={`form-control ${
                    errors.phone && touched.phone ? 'is-invalid' : ''
                  }`}
                  placeholder="1234567890"
                />
                <ErrorMessage
                  name="phone"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              {/* Number Input */}
              <div className="col-md-6 mb-3">
                <label htmlFor="age" className="form-label">
                  Age *
                </label>
                <Field
                  type="number"
                  name="age"
                  className={`form-control ${
                    errors.age && touched.age ? 'is-invalid' : ''
                  }`}
                  placeholder="Enter age"
                />
                <ErrorMessage
                  name="age"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              {/* Date Input */}
              <div className="col-md-6 mb-3">
                <label htmlFor="dateOfBirth" className="form-label">
                  Date of Birth *
                </label>
                <Field
                  type="date"
                  name="dateOfBirth"
                  className={`form-control ${
                    errors.dateOfBirth && touched.dateOfBirth ? 'is-invalid' : ''
                  }`}
                />
                <ErrorMessage
                  name="dateOfBirth"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              {/* Select Dropdown */}
              <div className="col-md-6 mb-3">
                <label htmlFor="country" className="form-label">
                  Country *
                </label>
                <Field
                  as="select"
                  name="country"
                  className={`form-select ${
                    errors.country && touched.country ? 'is-invalid' : ''
                  }`}
                >
                  <option value="">Select country</option>
                  <option value="india">India</option>
                  <option value="usa">USA</option>
                  <option value="uk">UK</option>
                  <option value="canada">Canada</option>
                  <option value="australia">Australia</option>
                </Field>
                <ErrorMessage
                  name="country"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              {/* Radio Buttons */}
              <div className="col-md-6 mb-3">
                <label className="form-label d-block">Gender *</label>
                <div className="form-check form-check-inline">
                  <Field
                    type="radio"
                    name="gender"
                    value="male"
                    className="form-check-input"
                    id="genderMale"
                  />
                  <label className="form-check-label" htmlFor="genderMale">
                    Male
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <Field
                    type="radio"
                    name="gender"
                    value="female"
                    className="form-check-input"
                    id="genderFemale"
                  />
                  <label className="form-check-label" htmlFor="genderFemale">
                    Female
                  </label>
                </div>
                <div className="form-check form-check-inline">
                  <Field
                    type="radio"
                    name="gender"
                    value="other"
                    className="form-check-input"
                    id="genderOther"
                  />
                  <label className="form-check-label" htmlFor="genderOther">
                    Other
                  </label>
                </div>
                {errors.gender && touched.gender && (
                  <div className="text-danger small mt-1">{errors.gender}</div>
                )}
              </div>

              {/* Textarea */}
              <div className="col-12 mb-3">
                <label htmlFor="bio" className="form-label">
                  Bio
                </label>
                <Field
                  as="textarea"
                  name="bio"
                  rows="3"
                  className={`form-control ${
                    errors.bio && touched.bio ? 'is-invalid' : ''
                  }`}
                  placeholder="Tell us about yourself..."
                />
                <ErrorMessage
                  name="bio"
                  component="div"
                  className="invalid-feedback"
                />
              </div>

              {/* Conditional Field */}
              <div className="col-12 mb-3">
                <div className="form-check">
                  <Field
                    type="checkbox"
                    name="hasExperience"
                    className="form-check-input"
                    id="hasExperience"
                  />
                  <label className="form-check-label" htmlFor="hasExperience">
                    I have professional experience
                  </label>
                </div>
              </div>

              {values.hasExperience && (
                <div className="col-md-6 mb-3">
                  <label htmlFor="yearsOfExperience" className="form-label">
                    Years of Experience *
                  </label>
                  <Field
                    type="number"
                    name="yearsOfExperience"
                    className={`form-control ${
                      errors.yearsOfExperience && touched.yearsOfExperience
                        ? 'is-invalid'
                        : ''
                    }`}
                    placeholder="Enter years"
                  />
                  <ErrorMessage
                    name="yearsOfExperience"
                    component="div"
                    className="invalid-feedback"
                  />
                </div>
              )}

              {/* Field Array - Dynamic Skills */}
              <div className="col-12 mb-3">
                <label className="form-label fw-bold">Skills *</label>
                <FieldArray name="skills">
                  {({ push, remove }) => (
                    <div>
                      {values.skills.map((skill, index) => (
                        <div key={index} className="row mb-2 align-items-start">
                          <div className="col-md-5">
                            <Field
                              name={`skills[${index}].name`}
                              placeholder="Skill name (e.g., React)"
                              className={`form-control ${
                                errors.skills?.[index]?.name &&
                                touched.skills?.[index]?.name
                                  ? 'is-invalid'
                                  : ''
                              }`}
                            />
                            <ErrorMessage
                              name={`skills[${index}].name`}
                              component="div"
                              className="invalid-feedback"
                            />
                          </div>
                          <div className="col-md-5">
                            <Field
                              as="select"
                              name={`skills[${index}].level`}
                              className={`form-select ${
                                errors.skills?.[index]?.level &&
                                touched.skills?.[index]?.level
                                  ? 'is-invalid'
                                  : ''
                              }`}
                            >
                              <option value="">Select level</option>
                              <option value="beginner">Beginner</option>
                              <option value="intermediate">Intermediate</option>
                              <option value="advanced">Advanced</option>
                              <option value="expert">Expert</option>
                            </Field>
                            <ErrorMessage
                              name={`skills[${index}].level`}
                              component="div"
                              className="invalid-feedback"
                            />
                          </div>
                          <div className="col-md-2">
                            {values.skills.length > 1 && (
                              <button
                                type="button"
                                className="btn btn-danger btn-sm"
                                onClick={() => remove(index)}
                              >
                                Remove
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => push({ name: '', level: '' })}
                      >
                        + Add Skill
                      </button>
                      {typeof errors.skills === 'string' && (
                        <div className="text-danger small mt-1">{errors.skills}</div>
                      )}
                    </div>
                  )}
                </FieldArray>
              </div>

              {/* Terms Checkbox */}
              <div className="col-12 mb-3">
                <div className="form-check">
                  <Field
                    type="checkbox"
                    name="termsAccepted"
                    className={`form-check-input ${
                      errors.termsAccepted && touched.termsAccepted
                        ? 'is-invalid'
                        : ''
                    }`}
                    id="termsAccepted"
                  />
                  <label className="form-check-label" htmlFor="termsAccepted">
                    I accept the terms and conditions *
                  </label>
                  <ErrorMessage
                    name="termsAccepted"
                    component="div"
                    className="invalid-feedback"
                  />
                </div>
              </div>

              {/* Submit Button */}
              <div className="col-12">
                <button
                  type="submit"
                  className="btn btn-primary btn-lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Submitting...
                    </>
                  ) : (
                    'Submit Form'
                  )}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      {/* Display Submitted Data */}
      {submittedData && (
        <div className="mt-4">
          <div className="alert alert-success">
            <h5 className="alert-heading">✓ Form Submitted Successfully!</h5>
            <hr />
            <pre className="mb-0">
              {JSON.stringify(submittedData, null, 2)}
            </pre>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormikDemo;
